import React from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
} from '@tanstack/react-table';
import { Table, TableHead, TableRow, TableCell, TableBody, TableFooter, TablePagination } from '@mui/material';
import { IAchSettlement } from '@/stack-shared/interfaces/model/ach-settlement.interface';

interface AchSettlementTableProps {
  data: IAchSettlement[];
}

const AchSettlementTable: React.FC<AchSettlementTableProps> = ({ data }) => {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const columns = React.useMemo(
    () => [
      {
        accessorKey: 'achDate',
        header: 'Date',
        cell: (info: any) => new Date(info.getValue()).toLocaleDateString(),
      },
      {
        accessorKey: 'merchantId',
        header: 'Merchant ID',
      },
      {
        accessorKey: 'description',
        header: 'Description',
      },
      {
        accessorKey: 'terminal',
        header: 'Terminal',
      },
      {
        accessorKey: 'dispensed',
        header: 'Dispensed',
        cell: (info: any) => {
          const value = Number(info.getValue()) || 0; // Ensure numeric value
          return `$${value.toFixed(2)}`;
        },
      },
      {
        accessorKey: 'surcharged',
        header: 'Surcharged',
        cell: (info: any) => {
          const value = Number(info.getValue()) || 0; // Ensure numeric value
          return `$${value.toFixed(2)}`;
        },
      },
      {
        accessorKey: 'surchargeAmount',
        header: 'Surcharge Amount',
        cell: (info: any) => {
          const value = Number(info.getValue()) || 0; // Ensure numeric value
          return `$${value.toFixed(2)}`;
        },
      },
    ],
    []
  );

  const table = useReactTable({
    data,
    columns,
    state: { sorting, pagination },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: false, // This is client-side
    pageCount: Math.ceil(data.length / pagination.pageSize),
  });

  return (
    <>
      <Table>
        <TableHead>
          {table.getHeaderGroups().map(headerGroup => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <TableCell
                  key={header.id}
                  onClick={header.column.getToggleSortingHandler()}
                  style={{ cursor: 'pointer' }}
                >
                  {header.isPlaceholder ? null : (
                    <>
                      {header.column.columnDef.header}{' '}
                      {header.column.getIsSorted() ? (header.column.getIsSorted() === 'asc' ? '🔼' : '🔽') : ''}
                    </>
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableHead>
        <TableBody>
          {table.getRowModel().rows.map(row => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell: any) => (
                <TableCell key={cell.id}>
  {cell.column.columnDef.cell ? cell.column.columnDef.cell(cell) : cell.getValue()}
</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              count={data.length}
              rowsPerPage={pagination.pageSize}
              page={pagination.pageIndex}
              onPageChange={(_, newPage) => table.setPageIndex(newPage)}
              onRowsPerPageChange={e => table.setPageSize(Number(e.target.value))}
              rowsPerPageOptions={[5, 10, 20]}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </>
  );
};

export default AchSettlementTable;
